import React, { useEffect, useRef, useState } from 'react'

import { getErrorMessage } from 'utils'
import { useEncodingDevice } from 'contexts'
import { useCanAccess, useCurrentCompany } from 'hooks'
import { useReceivePackage } from 'hooks/api'
import { api } from 'services'

import { useDispatch } from 'store/hooks'
import { endScanSesion, startScanSession } from 'store/scan-session/actions'
import type {
  IValidationScanSessionMessageCallbackParams,
  IValidationStartScanSessionCallbackParams,
} from 'store/scan-session/types'

import { BoxIcon, CheckIcon, ChevronDownIcon, InfoIcon, PackageIcon, XIcon } from 'lucide-react'

import { useTheme, Box, Collapse, Divider, IconButton, Link, Stack, Typography } from '@mui/material'

import { Button, Card, Modal, Alert } from '@repo/ui'
import {
  EmptyState,
  InfoTooltip,
  PACKAGE_HANDLE_URL_RE,
  ReaderSetupPanel,
  ScanSourceRow,
} from 'components'
// Direct file import, NOT the `components` barrel — this file is IN that barrel, so importing
// back through it is a cycle. Same reason `ScanSessionBlock` imports its own neighbours directly.
import { ScanSessionQRCode } from 'components/feedback/modals/ScanSessionBlock/ScanSessionBlock'

interface ReceivePackageModalProps {
  open: boolean
  onClose: () => void
}

/** One package resolved from a scanned handle this modal session. */
interface ScannedPackage {
  id: string
  name: string
  /**
   * VERA-455 — this box is ALREADY on hand; it is not waiting for the Receive button.
   *
   * The two sources in this modal genuinely differ here, and the list has to show it rather
   * than flatten it. A desk-reader scan STAGES a box (the operator is standing at the confirm
   * button, so a batch-then-confirm is the natural shape). A phone tap RECEIVES it outright —
   * the operator is at the dock with a box in one hand, and staging into a list on a screen
   * they cannot see would mean walking back to press a button, which is the entire problem the
   * phone path exists to solve.
   *
   * So a received row shows what HAPPENED and the confirm button ignores it. Rendering the two
   * identically would be the worse failure in both directions: it would either promise a
   * receipt that has not happened, or invite a second confirm for one that already has.
   */
  received?: boolean
  /** Tags moved by a phone receipt. Absent for a staged row — nothing has moved yet. */
  receivedTags?: number
}

/**
 * VRG-140 Slice 3 (VERA-891): company receipt-scan modal. Scan a box's handle label, then receive
 * that package into inventory — `POST /shop/tags/inventory/receive` per scanned package, which
 * flips that package's tags Incoming -> Available.
 *
 * VERA-455 — THE SCAN SOURCES ARE THE SHARED ONES NOW. This used to hand-roll its own surface:
 * a segmented "Scan with reader / Scan with phone" tab pair, a bespoke reader status row, and a
 * "Simulate scan (dev only)" text box that fed a typed package id through the real resolver.
 * All three are gone.
 *
 *  - The TABS were a fourth copy of a row that already existed three times (VRG-322), and they
 *    modelled the sources as MODES. They are not modes: a reader ingests whenever it is plugged
 *    in, and selecting the other tab turned the working source off (see the subscription below).
 *  - The SIMULATE box let a package be received by typing its uuid, with no scan and therefore
 *    no evidence anyone was holding the box. `tag_possession_events` is an audit trail, so that
 *    is a way to write a receipt that never happened. It also shipped to any environment where
 *    `VITE_DEV_SESSION` was set, not just local dev.
 *
 * What replaced them is `ScanSourceRow` — the same component and the same treatment the Create
 * Certificates block renders, which is what the parity is measured against.
 *
 * VERA-455 — THE PHONE ROW IS REAL NOW. It was a deliberate stub, because pairing a phone needs
 * a server-side session to pair INTO and the only thing `mobile-scan` could do with a `/pkg/`
 * handle was stage a box's TAGS into a CERTIFICATE session — a different job. That gap is
 * closed: `ScanSessionPurpose` has a `receive` member, and a box tapped in one of those sessions
 * is received rather than expanded. So this row now opens a real session and shows its QR.
 *
 * THE TWO SOURCES BEHAVE DIFFERENTLY ON PURPOSE — see `ScannedPackage.received`. The reader
 * stages; the phone receives on the tap. That is not an inconsistency to iron out later, it is
 * the difference between someone standing at this screen and someone at the dock who cannot see
 * it, and the copy on each row says which one it is.
 */
export const ReceivePackageModal = (props: ReceivePackageModalProps) => {
  const { open, onClose } = props
  const theme = useTheme()
  const dispatch = useDispatch()
  const { company } = useCurrentCompany()

  // The API refuses to OPEN a receive session without this permission, because the phone that
  // will use the session is unauthenticated and cannot present one of its own. Checked here too
  // so the row explains itself instead of showing a QR whose first tap would 403 at the dock —
  // §27: an in-context action the user cannot take stays visible, and names what is missing.
  const { isHasAccess: canReceive } = useCanAccess({ action: 'tags_inventory_receive' })

  const { isConnected: readerConnected, subscribeScan } = useEncodingDevice()
  // Setup guidance the OPERATOR asks for, same accordion the Create Certificates block and the
  // scan-source picker use. Closed by default: it is guidance for hardware they may not own, and
  // forcing it open whenever no reader is connected was deliberately undone in `ReaderSetupPanel`.
  const [readerExpanded, setReaderExpanded] = useState(false)
  const [scannedPackages, setScannedPackages] = useState<ScannedPackage[]>([])
  const [scanStatus, setScanStatus] = useState<{ message: string | null; severity: 'info' | 'error' }>(
    { message: null, severity: 'info' },
  )
  const [isResolvingScan, setIsResolvingScan] = useState(false)
  const scanResolvingRef = useRef(false)
  // EVERY id this modal has seen, from either source — the dedupe set, nothing more. It used to
  // double as the submit list, which cannot survive the phone path: a box the phone already
  // received must still be deduped, but must never be submitted again. The submit list is now
  // derived from the rows themselves (see `stagedPackages`), so the two cannot disagree.
  const scannedIdsRef = useRef<Set<string>>(new Set())
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // The phone pairing half. `scanDeviceURL` is the QR's payload, handed back when the session
  // opens; `phoneConnected` flips when a device actually registers against it.
  const [scanDeviceURL, setScanDeviceURL] = useState<string | null>(null)
  const [phoneConnected, setPhoneConnected] = useState(false)
  const [qrExpandedByUser, setQrExpandedByUser] = useState<boolean | null>(null)

  const receivePackage = useReceivePackage()

  /**
   * What a scan-session event means to THIS modal.
   *
   * Registered ONCE per open (handed to the saga), so it closes over the first render's state —
   * every update here is functional or through a ref, the same discipline `ScanSessionBlock`
   * documents at length for the same reason.
   */
  const scanSessionMessageHandler = (params: IValidationScanSessionMessageCallbackParams) => {
    if (params.deviceRegistered) {
      setPhoneConnected(true)
      return
    }

    // A BOX WAS RECEIVED, by a phone paired to this session. Its own channel rather than the
    // error channel every other package-level sentence rides, precisely so this success is not
    // rendered in the refusal treatment.
    const received = params.packageReceived
    if (received?.packageId) {
      const { packageId, packageName, received: movedTags } = received
      // The tap already happened at the dock, so this row is a REPORT, not a staging entry —
      // it never enters the submit list. Deduped like any other so a second tap on the same box
      // (which the server answers as "already on hand") does not add a second row.
      if (!scannedIdsRef.current.has(packageId)) {
        scannedIdsRef.current.add(packageId)
        setScannedPackages(prev => [
          ...prev,
          { id: packageId, name: packageName || 'Package', received: true, receivedTags: movedTags },
        ])
      }
      setScanStatus({ message: null, severity: 'info' })
      return
    }

    // The server's own package-level sentence — an unrecognised label, an empty box, a box that
    // was already on hand, another company's box. Shown verbatim rather than restated here: it
    // is written once, server-side, so the phone and this modal cannot describe the same box
    // differently (the same contract `announcePackageNotice` exists to hold).
    if (params.status === 'error' && typeof params.error === 'string' && params.error.trim()) {
      setScanStatus({ message: params.error, severity: 'error' })
      return
    }

    // The phone ended the run. The QR is stale and nothing further can land, so stand down
    // rather than sit open over a dead session looking live.
    if (params.sessionEnded) {
      setPhoneConnected(false)
      setScanDeviceURL(null)
    }
  }

  const startScanSessionCallback = (params: IValidationStartScanSessionCallbackParams) => {
    if (params.success) setScanDeviceURL(params.registerDeviceURL)
  }

  /**
   * One `receive` session per opening of this modal, torn down when it closes.
   *
   * NOT opened when the user lacks the permission: the API refuses it anyway (see `canReceive`),
   * and firing a request whose only possible outcome is a 403 to render a row that already says
   * so is noise in the log and nothing else.
   */
  useEffect(() => {
    // Reset FIRST, so a re-open never inherits the previous run's rows or its stale QR.
    setScannedPackages([])
    scannedIdsRef.current = new Set()
    setScanStatus({ message: null, severity: 'info' })
    setIsResolvingScan(false)
    scanResolvingRef.current = false
    setSubmitError(null)
    setIsSubmitting(false)
    setReaderExpanded(false)
    setScanDeviceURL(null)
    setPhoneConnected(false)
    setQrExpandedByUser(null)

    if (!open || !canReceive) return

    // No productId — a receive session names no product, which is exactly what VRG-265 made
    // possible by moving the product requirement to the mint boundary.
    dispatch(startScanSession(startScanSessionCallback, scanSessionMessageHandler, undefined, 'receive'))

    return () => {
      // The session outlives this modal otherwise, leaving a QR that a phone could still pair
      // to and receive through, with nothing on screen reporting it.
      dispatch(endScanSesion())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, canReceive])

  const handleClose = () => {
    onClose()
  }

  // Resolve a scanned package-handle URL (`/pkg/<packages.id>`) — same regex +
  // resolution path as LinkPackageModal's scan flow (VRG-140 Slice 2).
  const resolveScannedUrl = async (scannedUrl: string) => {
    if (scanResolvingRef.current) return
    const match = PACKAGE_HANDLE_URL_RE.exec(scannedUrl)
    if (!match) return
    const packageId = match[1]

    if (scannedIdsRef.current.has(packageId)) {
      // WHICH "already" it is matters. A box a phone has already RECEIVED is on hand and needs
      // nothing further, while a staged one is still waiting for the Receive button. Told the
      // same sentence, an operator would keep looking for the action on a box that is done.
      const alreadyReceived = scannedPackages.some(p => p.id === packageId && p.received)
      setScanStatus({
        message: alreadyReceived
          ? "You've already received this package."
          : "You've already scanned this package.",
        severity: 'info',
      })
      return
    }

    scanResolvingRef.current = true
    setIsResolvingScan(true)
    setScanStatus({ message: 'Reading package…', severity: 'info' })
    try {
      // Company-scoped resolve (the internal GET /packages/:id is gated by an
      // internal permission a company user doesn't hold) — server validates
      // the package belongs to this company + returns display info.
      const response = await api.get(`/shop/tags/inventory/package/${packageId}`)
      const status = response?.status
      const pkg = response?.data
      if (!pkg?.id || (typeof status === 'number' && (status < 200 || status >= 300))) {
        setScanStatus({ message: "That package label isn't recognized", severity: 'error' })
        return
      }

      if (company?.id && pkg.company_id && pkg.company_id !== company.id) {
        setScanStatus({
          message: `${pkg.name} belongs to a different company and can't be received here.`,
          severity: 'error',
        })
        return
      }

      scannedIdsRef.current.add(packageId)
      setScannedPackages((prev) => [...prev, { id: pkg.id, name: pkg.name }])
      setScanStatus({ message: null, severity: 'info' })
    } catch (err) {
      setScanStatus({ message: getErrorMessage(err), severity: 'error' })
    } finally {
      setIsResolvingScan(false)
      scanResolvingRef.current = false
    }
  }

  const handleRemoveScanned = (id: string) => {
    scannedIdsRef.current.delete(id)
    setScannedPackages((prev) => prev.filter((p) => p.id !== id))
  }

  // NO LONGER GATED ON A SELECTED METHOD. The segmented "Scan with reader / Scan with phone"
  // control this replaces modelled the sources as MODES, so subscribing was conditional on the
  // reader tab being the active one — and an operator who had switched to the phone tab (which
  // could not scan anything) had also silently switched the working source OFF. Sources are
  // additive, exactly as `ScanSessionBlock` treats them: the reader ingests whenever one is
  // plugged in, and nothing on screen turns it off.
  useEffect(() => {
    if (!open) return
    const unsub = subscribeScan((scannedUrl) => {
      resolveScannedUrl(scannedUrl)
    })
    return () => {
      unsub()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, subscribeScan, company])

  // Only the STAGED rows. A phone-received box is already on hand, so submitting it again would
  // be a second receipt for one arrival — and `transitionPackageTags` would move zero rows,
  // making the button report a success for something it did not do.
  const stagedPackages = scannedPackages.filter((p) => !p.received)

  const handleConfirmReceive = async () => {
    const packageIds = stagedPackages.map((p) => p.id)
    if (!packageIds.length || isSubmitting) return
    setIsSubmitting(true)
    setSubmitError(null)
    try {
      await Promise.all(packageIds.map((packageId) => receivePackage.mutateAsync({ packageId })))
      handleClose()
    } catch (err) {
      setSubmitError(getErrorMessage(err))
    } finally {
      setIsSubmitting(false)
    }
  }

  const selectedCount = stagedPackages.length
  // Whether the QR panel is open — SPACE ONLY. The phone can pair and receive regardless, and
  // the reader ingests regardless; nothing here turns a source off. Collapsed once a phone has
  // paired (there is nothing left to scan) and once a reader is connected (the operator already
  // has a working path, and the QR would push the box list off screen). An explicit toggle wins,
  // including after pairing — there is no disconnect event, so a phone that backgrounded would
  // otherwise latch "Connected" with no way to re-show the code.
  const qrOpen = qrExpandedByUser ?? (!readerConnected && !phoneConnected && Boolean(scanDeviceURL))

  return (
    <Modal
      title="Receive package"
      open={open}
      onClose={handleClose}
      actions={
        <>
          <Button disableElevation variant="secondary" onClick={handleClose} disabled={isSubmitting}>
            {/* "Cancel" is a lie once a phone has already received something — it reads as
                undoing a receipt that has happened and cannot be undone from here. It stays
                "Cancel" whenever there is still staged work to abandon, because then it is
                accurate. */}
            {selectedCount === 0 && scannedPackages.length > 0 ? 'Done' : 'Cancel'}
          </Button>
          <Button
            disableElevation
            onClick={handleConfirmReceive}
            disabled={selectedCount === 0 || isSubmitting}
            loading={isSubmitting}
          >
            {`Receive${selectedCount > 0 ? ` ${selectedCount}` : ''} package${selectedCount === 1 ? '' : 's'}`}
          </Button>
        </>
      }
    >
      <Stack spacing={4}>
        {submitError && <Alert severity="error" description={submitError} />}

        {/* SCAN SOURCES — the same block Create Certificates renders, on the same shared
            `ScanSourceRow` (VRG-322), rather than the segmented "Scan with reader / Scan with
            phone" tabs this replaces. The tabs were wrong twice over: they made the two sources
            look mutually exclusive when they are additive, and one of the two tabs led to a
            "coming soon" placeholder, so half the control was an option that did nothing.

            READER FIRST, kept — but the REASON changed and is worth stating rather than leaving
            the old one to rot. It used to be `ScanSourceRow`'s "never lead with an option the
            user cannot act on", because the phone row was a stub. The phone works now
            (VERA-455), so that argument is spent. What replaces it: the reader is the source
            that pairs with THIS SCREEN, and the operator reading this list is at this screen.
            A phone operator is at the dock and never sees this ordering at all. */}
        <Typography variant="body2">Scan sources</Typography>

        <ScanSourceRow
          source="reader"
          label="Scan with a hardware reader"
          leading="status"
          connected={readerConnected}
          expanded={readerExpanded}
          onActivate={() => setReaderExpanded((v) => !v)}
          adornment={
            /* `term` AND `text`, matching the Create Certificates row verbatim: the glossary
               already defines what a reader IS (§44), so the footer links there instead of this
               call site retyping it, and the body says the local thing the definition cannot. */
            <InfoTooltip
              term="reader"
              text="A Vera desk reader plugged into this computer, reached through the Vera Reader Helper. Not connected means no reader is plugged in, or the helper is not running."
            >
              <Box
                component="span"
                // Stopped, or reaching for the explanation collapses the panel it explains.
                onClick={(e) => e.stopPropagation()}
                tabIndex={0}
                aria-label="How reader scanning works"
                sx={{ display: 'flex', cursor: 'help' }}
              >
                <InfoIcon strokeWidth={1} size={18} color={theme.palette.grey[800]} />
              </Box>
            </InfoTooltip>
          }
          trailing={
            <ChevronDownIcon
              strokeWidth={1}
              size={20}
              style={{
                flexShrink: 0,
                transform: readerExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease-in-out',
              }}
            />
          }
        >
          <Collapse in={readerExpanded}>
            <Divider />
            {/* The shared setup panel, same as the Create Certificates block and the connect
                modal. `onNavigate` closes this modal first: both of its actions leave the page,
                and without it the operator arrives at the shop with this dialog over it. */}
            <ReaderSetupPanel connected={readerConnected} onNavigate={handleClose} />
          </Collapse>
        </ScanSourceRow>

        {/* THE PHONE ROW, LIVE (VERA-455). This was a stub carrying a `TODO(VERA-455)` anchor,
            because pairing a phone needs a server-side session to pair INTO and the only thing
            `mobile-scan` did with a `/pkg/` handle was stage a box's tags into a CERTIFICATE
            session. Both halves exist now — `ScanSessionPurpose` has a `receive` member, and a
            box tapped in one of those is received — so the anchor is retired rather than moved.

            THE COPY SAYS THE TAP RECEIVES. That is the one thing an operator cannot infer from
            a QR sitting next to a "Receive 2 packages" button, and getting it wrong in either
            direction is costly: someone who expects staging walks back to a screen where the
            box is already done, and someone who expects an instant receipt but got staging
            leaves the dock with nothing recorded. */}
        <ScanSourceRow
          source="phone"
          label="Scan with your smartphone"
          leading="status"
          connected={phoneConnected}
          secondary={!canReceive}
          status={
            !canReceive
              ? 'Permission needed'
              : phoneConnected
                ? 'Connected'
                : 'Not connected'
          }
          expanded={canReceive ? qrOpen : undefined}
          onActivate={canReceive ? () => setQrExpandedByUser(!qrOpen) : undefined}
          adornment={
            canReceive ? (
              /* No glossary term covers "pair a phone to a scan session by QR", so this is a
                 literal `text` body — the control-explanation case the prop exists for. Matches
                 the Create Certificates row's wording for the pairing half, then says the thing
                 that is specific to THIS surface: the tap is the receipt. */
              <InfoTooltip text="Scan the QR code with your phone camera to pair that phone with this session. It opens the Vera app, which has to be installed. Tapping a box with a paired phone receives it straight away.">
                <Box
                  component="span"
                  // Stopped, or reaching for the explanation collapses the panel it explains.
                  onClick={(e) => e.stopPropagation()}
                  tabIndex={0}
                  aria-label="How phone scanning works"
                  sx={{ display: 'flex', cursor: 'help' }}
                >
                  <InfoIcon strokeWidth={1} size={18} color={theme.palette.grey[800]} />
                </Box>
              </InfoTooltip>
            ) : undefined
          }
          trailing={
            canReceive ? (
              <Link
                component="button"
                type="button"
                underline="none"
                variant="body3"
                // Stopped, or the click toggles here AND again on the row it bubbles into,
                // leaving the panel exactly as it was.
                onClick={(e) => {
                  e.stopPropagation()
                  setQrExpandedByUser(!qrOpen)
                }}
                sx={{ color: 'text.secondary', '&:hover': { color: theme.palette.brand[600] } }}
              >
                {qrOpen ? 'Hide QR' : 'Show QR'}
              </Link>
            ) : undefined
          }
        >
          {canReceive ? (
            <Collapse in={qrOpen}>
              <Divider />
              <Stack spacing={4} sx={{ minHeight: 218, pt: 4, pb: 4 }}>
                {scanDeviceURL && <ScanSessionQRCode registerDeviceURL={scanDeviceURL} />}
                {/* §15 — periods, not em-dashes, and `lineHeight: 1.5` so two lines of guidance
                    do not read as a dense block. */}
                <Typography
                  variant="body3"
                  color="text.secondary"
                  sx={{ lineHeight: 1.5, textAlign: 'center', px: 4 }}
                >
                  Tapping a box with your phone receives it right away. It does not wait for the
                  button below.
                </Typography>
              </Stack>
            </Collapse>
          ) : (
            <Typography
              variant="body3"
              color="text.secondary"
              // `pl: 8` = 32px = the status dot (20px) plus the row's own `spacing={3}` (12px),
              // so this hangs exactly on the label above rather than near it. Measured, not
              // guessed: at `pl: 9` it sat 4px past the label, which reads as a misalignment
              // rather than an indent.
              sx={{ lineHeight: 1.5, pb: 3, pr: 4, pl: 8 }}
            >
              You need the “Receive inventory” permission to receive boxes with a phone. Ask an
              admin to grant it.
            </Typography>
          )}
        </ScanSourceRow>

        {scanStatus.message && (
          <Alert severity={scanStatus.severity} description={scanStatus.message} />
        )}

        <Stack spacing={2}>
          <Typography variant="body2">
            {scannedPackages.length > 0
              ? `${scannedPackages.length} package${scannedPackages.length === 1 ? '' : 's'} scanned`
              : 'Packages scanned'}
          </Typography>
          {scannedPackages.length === 0 ? (
            <Box
              sx={{
                border: `1px solid ${theme.palette.primary[100]}`,
                borderRadius: 2,
                py: 6,
              }}
            >
              <EmptyState
                icon={<PackageIcon strokeWidth={1} size={32} color={theme.palette.primary.main} />}
                title="No packages scanned yet"
                text={
                  isResolvingScan
                    ? 'Reading package…'
                    : "Scan a box's handle label to receive it into inventory."
                }
              />
            </Box>
          ) : (
            <Card sx={{ p: 0, overflow: 'hidden' }}>
              <Stack
                divider={<div style={{ borderTop: `1px solid ${theme.palette.primary[100]}` }} />}
                sx={{ maxHeight: 280, overflowY: 'auto' }}
              >
                {scannedPackages.map((pkg) => (
                  <Stack
                    key={pkg.id}
                    direction="row"
                    alignItems="center"
                    spacing={3}
                    sx={{
                      px: 5,
                      py: 3,
                      '&:hover .remove-scanned-package': { opacity: 1 },
                    }}
                  >
                    <BoxIcon strokeWidth={1} size={16} color={theme.palette.text.secondary} />
                    <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body3" noWrap>
                        {pkg.name}
                      </Typography>
                    </Stack>
                    {/* A PHONE-RECEIVED ROW IS A REPORT, NOT A SELECTION — so it says what
                        happened and drops the remove control. Removing it would suggest the
                        receipt can be taken back from here, and it cannot: the tags have moved
                        and `tag_possession_events` has the row. The bare check beside a staged
                        box means "scanned, will be received"; the word is what tells the two
                        apart at a glance. */}
                    {pkg.received ? (
                      <Typography variant="body3" color={theme.palette.brand[500]} noWrap>
                        {pkg.receivedTags
                          ? `Received · ${pkg.receivedTags} tag${pkg.receivedTags === 1 ? '' : 's'}`
                          : 'Received'}
                      </Typography>
                    ) : (
                      <>
                        <CheckIcon strokeWidth={1.5} size={18} color={theme.palette.brand[500]} />
                        <IconButton
                          size="small"
                          className="remove-scanned-package"
                          onClick={() => handleRemoveScanned(pkg.id)}
                          sx={{ opacity: 0, transition: 'opacity 150ms' }}
                          aria-label={`Remove ${pkg.name}`}
                        >
                          <XIcon strokeWidth={1} size={16} />
                        </IconButton>
                      </>
                    )}
                  </Stack>
                ))}
              </Stack>
            </Card>
          )}
        </Stack>
      </Stack>
    </Modal>
  )
}
